import React, { createContext, useMemo, useState } from 'react';
import { TiktokAudioCollection, usePaginatedFetch, getCollections } from '@round/api';
import { FCWithChildren } from '../../../../../../../../App.types';
import useAbortableEffect from '../../../../../../../../Hooks/useAbortableEffect';

type CollectionsCellContextValues = {
    collectionsCount: number;
    collections: TiktokAudioCollection[];
    collectionsLoading: boolean;
    loadMoreCollections: () => Promise<void>;
    collectionsSearchQuery: string;
    setCollectionsSearchQuery: React.Dispatch<React.SetStateAction<string>>;
};

export const CollectionsCellContext = createContext<CollectionsCellContextValues | null>(null);
export const CollectionsCellContextProvider: FCWithChildren = ({ children }) => {
    const [collectionsSearchQuery, setCollectionsSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const {
        data: collections,
        count: collectionsCount,
        loading: collectionsLoading,
        getCurrentPage,
        hasNextPage,
    } = usePaginatedFetch(getCollections, { search: collectionsSearchQuery, page });

    useAbortableEffect(
        (signal) => {
            getCurrentPage({ signal });
        },
        [getCurrentPage]
    );

    const values: CollectionsCellContextValues = useMemo(
        () => ({
            collections,
            collectionsCount,
            collectionsLoading,
            loadMoreCollections: async () => {
                if (hasNextPage && !collectionsLoading) {
                    setPage((page) => page + 1);
                }
            },
            collectionsSearchQuery,
            setCollectionsSearchQuery: (searchQuery) => {
                if (collectionsSearchQuery !== searchQuery) {
                    setPage(1);
                    setCollectionsSearchQuery(searchQuery);
                }
            },
        }),
        [collections, collectionsCount, collectionsLoading, collectionsSearchQuery, hasNextPage]
    );

    return <CollectionsCellContext.Provider value={values}>{children}</CollectionsCellContext.Provider>;
};
